import React, { useEffect, useRef, useState } from 'react';
import { CategoryData } from 'types/CategoryData';
import { StyledCategoryMenu } from './style';
import { RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PublicRoute from 'enums/publicRoutes';

interface CategoryMenuProps {
  categories: CategoryData[];
  onCloseMenu: () => void;
}

const isVisible = (c: CategoryData | null | undefined): boolean =>
  (c?.product_count ?? 0) > 0;

const hasVisibleChildren = (subs?: CategoryData[]) =>
  subs?.some(isVisible) ?? false;

const CategoryMenu = ({ categories, onCloseMenu }: CategoryMenuProps) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<CategoryData | null>(
    null
  );
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<CategoryData | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleMouseOverCategory = (category: CategoryData) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);
  };

  const handleMouseOverSubCategory = (subcategory: CategoryData) => {
    setSelectedSubCategory(subcategory);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setSelectedCategory(null);
      setSelectedSubCategory(null);
    }
  };

  const closeMenuAndNavigate = (params: {
    'main-category'?: string;
    'sub-category'?: string;
    'sub-subcategory'?: string;
  }) => {
    onCloseMenu();
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    navigate(
      `${PublicRoute.PRODUCTS_ALL}?${new URLSearchParams(params).toString()}`
    );
  };

  const handleClickCategory = (category: CategoryData) => {
    if (!category?.id) return;
    closeMenuAndNavigate({
      'main-category': String(category.id),
    });
  };

  const handleClickSubCategory = (subcategory: CategoryData) => {
    if (!selectedCategory?.id || !subcategory?.id) return;
    closeMenuAndNavigate({
      'main-category': String(selectedCategory.id),
      'sub-category': String(subcategory.id),
    });
  };

  const handleClickSubSubCategory = (subsubcategory: CategoryData) => {
    if (
      !selectedCategory?.id ||
      !selectedSubCategory?.id ||
      !subsubcategory?.id
    )
      return;
    closeMenuAndNavigate({
      'main-category': String(selectedCategory.id),
      'sub-category': String(selectedSubCategory.id),
      'sub-subcategory': String(subsubcategory.id),
    });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const visibleCategories = categories.filter(isVisible);

  const visibleSubCategories =
    selectedCategory?.subcategories?.filter(isVisible) ?? [];

  const visibleSubSubCategories =
    selectedSubCategory?.subcategories?.filter(isVisible) ?? [];

  const renderMenu = () => (
    <div className="wrapper-menu">
      <div className="menu">
        <div className="category">
          <ul>
            {visibleCategories.map((category, index) => (
              <li
                key={category.id}
                onMouseEnter={() => handleMouseOverCategory(category)}
                onClick={() => handleClickCategory(category)}
                className={
                  index === visibleCategories.length - 1 ? 'no-underline' : ''
                }
              >
                {category.name}{' '}
                <span className="product-count">
                  ({category.product_count})
                </span>
                {hasVisibleChildren(category.subcategories) && (
                  <RightOutlined className="icon" />
                )}
              </li>
            ))}
          </ul>
        </div>
        {visibleSubCategories.length > 0 && (
          <div className="subcategory">
            <ul>
              {visibleSubCategories.map((subcategory, index) => (
                <li
                  key={subcategory.id}
                  onMouseEnter={() => handleMouseOverSubCategory(subcategory)}
                  onClick={() => handleClickSubCategory(subcategory)}
                  className={
                    index === visibleSubCategories.length - 1
                      ? 'no-underline'
                      : ''
                  }
                >
                  {subcategory.name}{' '}
                  <span className="product-count">
                    ({subcategory.product_count})
                  </span>
                  {hasVisibleChildren(subcategory.subcategories) && (
                    <RightOutlined className="icon" />
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {visibleSubSubCategories.length > 0 && (
          <div className="subsubcategory">
            <ul>
              {visibleSubSubCategories.map((subsubcategory, index) => (
                <li
                  key={subsubcategory.id}
                  onClick={() => handleClickSubSubCategory(subsubcategory)}
                  className={
                    index === visibleSubSubCategories.length - 1
                      ? 'no-underline'
                      : ''
                  }
                >
                  {subsubcategory.name}{' '}
                  <span className="product-count">
                    ({subsubcategory.product_count})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <StyledCategoryMenu
      ref={menuRef}
      itemProp={Math.ceil(visibleCategories?.length * 46.47).toString()}
    >
      {renderMenu()}
    </StyledCategoryMenu>
  );
};

export default CategoryMenu;
